
import {defineComponent} from "vue";
import {Form} from "vee-validate";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import BaseSubmittingOverlay from "@/components/UI/Bootstrap/BaseSubmittingOverlay.vue";
import AutoSaveFeedback from "@/components/UI/AutoSaveFeedback.vue";
import {PropType} from "vue/dist/vue";
import {UIStateDto} from "@/dtos/UIStateDto";
import {AutoSaveFeedbackStatusEnum} from "@/models/enum/AutoSaveFeedbackStatusEnum";
import {scrollToFirstInvalidField} from "@/helpers/functions/forms";

export default defineComponent({
  name: "GenericFormWrapper",
  components: { Form, AlertError2, BaseSubmittingOverlay, AutoSaveFeedback },
  props: {
    pageUI: {
      type: Object as PropType<UIStateDto>,
      required: true
    },
    submitData: {
      type: Function,
      required: true
    },
    alignButtonsRight: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * If set to true, the fragment containing the save button, the autosavefeedback, and any extra buttons
     * defined in the buttons slot, will not be displayed.
     */
    hideButtons: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * CSS class to be added to the save button, for example 'btn-sm'
     */
    buttonClass: {
      type: String,
      required: false,
      default: ''
    },
    /**
     * If set to true, the submitting overlay and the alert error are not displayed by this component.
     * They are supposed to be handled by the calling component instead.
     * This can be used if for example this form is conditionally rendered, but there are delete buttons
     * that are always rendered, and these can result in errors to be displayed, and they require the submitting
     * overlay as well.
     */
    hideOverlayAndAlert: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If set to true, message about unsaved date, saving... is never displayed.
     */
    hideFeedbackStatus: {
      type: Boolean,
      required: false,
      default: false
    },
    submitButtonTranslationKey: {
      type: String,
      required: false,
      default: 'save'
    },
    /**
     * If set to true, the form fills the height offered by its parent, only the content slot scrolls,
     * and the row with the save button stays pinned to the bottom of that area.
     * Intended for forms rendered inside an offcanvas, where the save button must stay reachable
     * without scrolling to the end of a long form.
     *
     * The parent has to offer a height for this to have any effect (an offcanvas body does).
     */
    pinnedFooter: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    AutoSaveFeedbackStatusEnum() {
      return AutoSaveFeedbackStatusEnum;
    },
    buttonsRowClass(): string {
      if (this.alignButtonsRight) {
        return 'ms-auto';
      }
      return '';
    },
    /**
     * The vee-validate Form and the native form sit between the height-carrying wrapper and the
     * content, so they have to take part in the flex chain as well; without them the content never
     * gets a bounded height and nothing scrolls.
     */
    pinnedFormClass(): string {
      return this.pinnedFooter ? 'd-flex flex-column pinned-fill' : '';
    },
  },
  methods: {
    /**
     * A failed validation blocks the submit without saying so anywhere near the button that was
     * pressed. Forms here run long enough that the offending field is usually off screen, so it is
     * brought into view instead of leaving the user with a save that silently does nothing.
     */
    async onInvalidSubmit(): Promise<void> {
      await scrollToFirstInvalidField(this.$refs.formElement as HTMLElement);
    },
  },
});
